/**
 * Cart Drawer Component
*/

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.addEventListener('click', (e) => {
      const closeTrigger = e.target.closest('[data-cart-close]');
      if (!closeTrigger) return;
      e.preventDefault();
      this.close();
    });
    
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('.header__cart, [data-cart-open]');
    cartLinks.forEach(cartLink => {
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add('sf-cart-opened', 'active');
    }, 10);

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    this.syncFooterSpace();

    if (window.cartPromocode) {
      window.cartPromocode.loadSavedCode();
    }
  }

  close() {
    this.classList.remove('sf-cart-opened', 'active');
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  }

  /**
   * The drawer footer is bottom-anchored and overlaps the scrolling item list,
   * which reserves room for it with padding-bottom. That height is not fixed —
   * the Yango activation notice makes the footer far taller — so measure it and
   * hand the value to CSS as --cart-footer-space instead of a magic number.
   * Runs after every cart render because the footer node is replaced each time.
   */
  syncFooterSpace() {
    const footer = this.querySelector('.form-wrap__footer');
    if (!footer) return;

    const publish = () => {
      const height = footer.offsetHeight;
      if (height) this.style.setProperty('--cart-footer-space', `${height}px`);
    };

    publish();

    // Keeps up with the notice wrapping differently on resize or late fonts.
    if (this.footerSpaceObserver) this.footerSpaceObserver.disconnect();
    if (typeof ResizeObserver === 'function') {
      this.footerSpaceObserver = new ResizeObserver(publish);
      this.footerSpaceObserver.observe(footer);
    }
  }

  renderContents(parsedState) {
    window.__yasminaCartState = parsedState;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      
      if (parsedState.sections && parsedState.sections[section.id]) {
        const newContent = this.getSectionInnerHTML(
          parsedState.sections[section.id], 
          section.selector
        );
        if (newContent) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(newContent, 'text/html');
          sectionElement.replaceChildren(...Array.from(doc.body.childNodes).map(node => node.cloneNode(true)));
        }
      }
    });
    
    this.updateCartCountDisplay(parsedState.item_count);
    this.updateCartTotalsDisplay(parsedState.total_price);
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (parsedState.item_count === 0) {
        cartDrawer.classList.add('is-empty');
      } else {
        cartDrawer.classList.remove('is-empty');
      }
    }
    
    const emptyMsg = document.querySelector('[data-cart-empty]');
    const itemsWrapper = document.querySelector('[data-cart-items-wrapper]');
    if (emptyMsg && itemsWrapper) {
      if (parsedState.item_count === 0) {
        emptyMsg.style.display = '';
        itemsWrapper.style.display = 'none';
      } else {
        emptyMsg.style.display = 'none';
        itemsWrapper.style.display = '';
      }
    }
    
    const savedCode = window.SecurityUtils 
      ? window.SecurityUtils.getValidatedLocalStorage('checkout_discount_code')
      : localStorage.getItem('checkout_discount_code');
    const checkoutButtons = document.querySelectorAll('[data-checkout]');
    checkoutButtons.forEach(btn => {
      let href = '/checkout';
      if (savedCode) {
        href = href + '?discount=' + encodeURIComponent(savedCode);
      }
      btn.setAttribute('href', href);
    });

    if (window.cartPromocode) {
      window.cartPromocode.loadSavedCode();
    }

    this.applySingletonCartLocks();
    this.initGuaranteeBlocks();
    this.syncFooterSpace();

    setTimeout(() => {
      this.open();
    }, 150);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: 'cart-drawer',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  formatMoney(cents) {
    return formatStoreMoney(cents);
  }

  updateCartCountDisplay(count) {
    if (count === undefined) return;

    document.querySelectorAll('[data-cart-count]').forEach((cartCount) => {
      cartCount.textContent = count;
      cartCount.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  updateCartTotalsDisplay(totalPrice) {
    if (totalPrice === undefined) return;

    const formattedPrice = this.formatMoney(totalPrice);
    document.querySelectorAll('[data-cart-total]').forEach((node) => {
      node.textContent = formattedPrice;
    });
    document.querySelectorAll('[data-cart-subtotal]').forEach((node) => {
      node.textContent = formattedPrice;
    });

    const isZero = totalPrice === 0;
    document.querySelectorAll('[data-checkout-button]').forEach((btn) => {
      if (isZero) {
        btn.classList.add('is-disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('tabindex', '-1');
      } else {
        btn.classList.remove('is-disabled');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('tabindex');
      }
    });
  }

  applySingletonCartLocks() {
    const singletonIds = Array.isArray(window.__yasminaSingletonVariantIds)
      ? window.__yasminaSingletonVariantIds.map((id) => String(id))
      : [];

    if (!singletonIds.length) return;

    document.querySelectorAll('[data-cart-item]').forEach((item) => {
      const lineKey = item.getAttribute('data-line-item-key') || '';
      const variantId = item.getAttribute('data-cart-item-variant-id') || lineKey.split(':')[0] || '';
      if (!singletonIds.includes(String(variantId))) return;

      item.setAttribute('data-cart-item-singleton', 'true');

      const counter = item.querySelector('.cart__counter');
      const input = item.querySelector('[data-cart-quantity]');
      const dec = item.querySelector('[data-cart-decrease]');
      const inc = item.querySelector('[data-cart-increase]');

      if (counter) {
        counter.setAttribute('data-cart-singleton-counter', 'true');
      }
      if (input) {
        input.value = '1';
        input.setAttribute('readonly', 'true');
        input.setAttribute('aria-readonly', 'true');
        input.setAttribute('tabindex', '-1');
        input.style.pointerEvents = 'none';
        input.style.opacity = '0.7';
      }
      if (dec) {
        dec.setAttribute('aria-disabled', 'true');
        dec.setAttribute('tabindex', '-1');
        dec.style.pointerEvents = 'none';
        dec.style.opacity = '0.35';
        dec.style.cursor = 'default';
      }
      if (inc) {
        inc.setAttribute('aria-disabled', 'true');
        inc.setAttribute('tabindex', '-1');
        inc.style.pointerEvents = 'none';
        inc.style.opacity = '0.35';
        inc.style.cursor = 'default';
      }
    });
  }

  getGuaranteeConfig() {
    const el = document.getElementById('guarantee-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  initGuaranteeBlocks() {
    // TODO: temporarily disabled
    return;
    const config = this.getGuaranteeConfig();
    if (!config) return;

    // Remove any previously injected guarantee blocks
    document.querySelectorAll('.guarantee-block').forEach(b => b.remove());

    const cart = window.__yasminaCartState;
    const cartItems = (cart && Array.isArray(cart.items)) ? cart.items : [];

    // Find which guarantee variants are already in cart
    const guaranteeInCart = {};
    const allGuaranteeVariantIds = new Set(
      Object.values(config.variants).map(v => String(v.id))
    );
    cartItems.forEach(item => {
      if (allGuaranteeVariantIds.has(String(item.variant_id))) {
        guaranteeInCart[String(item.variant_id)] = item.quantity;
      }
    });

    // Group speaker items by type
    const speakerItems = document.querySelectorAll('[data-speaker-type]');
    const groups = {};
    speakerItems.forEach(el => {
      const type = el.getAttribute('data-speaker-type');
      if (!groups[type]) {
        groups[type] = { totalQty: 0, lastElement: null, handle: null };
      }
      groups[type].totalQty += parseInt(el.getAttribute('data-speaker-qty') || '1', 10);
      groups[type].lastElement = el;
      groups[type].handle = el.getAttribute('data-speaker-handle');
    });

    // Render a guarantee block after the last item of each speaker type
    Object.entries(groups).forEach(([type, group]) => {
      const guaranteeVariantId = config.speakerToGuarantee[group.handle];
      if (!guaranteeVariantId) return;

      const variant = config.variants[String(guaranteeVariantId)];
      if (!variant) return;

      const currentQty = guaranteeInCart[String(guaranteeVariantId)] || 0;
      const isActive = currentQty > 0;
      const displayQty = isActive ? currentQty : 1;
      const showCounter = group.totalQty > 1;
      const price = this.formatMoney(variant.price);

      const block = document.createElement('div');
      block.className = 'guarantee-block' + (showCounter ? ' has-counter' : '');
      block.setAttribute('data-guarantee-type', type);
      block.setAttribute('data-guarantee-variant-id', String(guaranteeVariantId));
      block.setAttribute('data-guarantee-max-qty', String(group.totalQty));

      block.innerHTML = `
        <div class="guarantee-block__header">
          <div class="guarantee-block__info">
            <div class="guarantee-block__text">
              <button type="button" class="guarantee-block__title-trigger" data-guarantee-info-open aria-label="Open guarantee details">
                <div class="guarantee-block__title-row">
                  <span class="guarantee-block__title">${this.escapeHtml(config.productTitle)}</span>
                  <svg class="guarantee-block__arrow" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.5 2.5L8 6L4.5 9.5" stroke="#191521" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </button>
              <span class="guarantee-block__subtitle">+1 year for ${this.escapeHtml(variant.title)}</span>
            </div>
            <span class="guarantee-block__price">${price}</span>
          </div>
          <button type="button" class="guarantee-block__toggle${isActive ? ' is-active' : ''}" data-guarantee-toggle="${type}" aria-label="Toggle guarantee">
            <div class="guarantee-block__toggle-circle"></div>
          </button>
        </div>
        ${showCounter ? `
        <div class="guarantee-block__footer">
          <div class="guarantee-block__counter">
            <button type="button" class="guarantee-block__counter-btn${displayQty <= 1 ? ' is-disabled' : ''}" data-guarantee-decrease="${type}" aria-label="Decrease guarantee quantity">
              <svg width="12" height="12" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.75 0.75H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <span class="guarantee-block__counter-qty" data-guarantee-qty-display="${type}">${displayQty}</span>
            <button type="button" class="guarantee-block__counter-btn${displayQty >= group.totalQty ? ' is-disabled' : ''}" data-guarantee-increase="${type}" aria-label="Increase guarantee quantity">
              <svg width="12" height="12" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.75 4.875H9M4.875 0.75V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <span class="guarantee-block__hint">Secure all speakers: an extra guarantee per unit (adds to standard warranty)</span>
        </div>` : ''}
      `;

      group.lastElement.insertAdjacentElement('afterend', block);
    });
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
    
    this.cartUpdateUnsubscriber = null;
    this.pendingQuantityUpdates = new Set();
    
    if (!window.__cartUpdateSubscribed) {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cart-items') {
          return;
        }
        return this.onCartUpdate();
      });
      window.__cartUpdateSubscribed = true;
      window.__cartUpdateUnsubscriber = this.cartUpdateUnsubscriber;
    }
    
    this.bindCartEvents();
    this.boundEventHandlers = new Map();
  }

  disconnectedCallback() {
    // Proper cleanup of event listeners
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
    
    if (this.boundEventHandlers) {
      this.boundEventHandlers.forEach((handler, key) => {
        const [eventType, selector] = key.split('::');
        document.removeEventListener(eventType, handler);
      });
      this.boundEventHandlers.clear();
    }
    
    window.__cartUpdateSubscribed = false;
  }
  
  bindCartEvents() {
    if (window.__cartEventsBound) return;
    window.__cartEventsBound = true;
    document.addEventListener('click', async (e) => {
      if (e.target.closest('[data-cart-increase]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-cart-increase]');
        if (btn.closest('[data-cart-item-singleton]')) return;
        const key = btn.dataset.cartIncrease;
        const input = document.querySelector(`[data-cart-quantity="${key}"]`);
        if (input) {
          const newQty = parseInt(input.value) + 1;
          await this.changeItemQuantity(key, newQty);
        }
      }
      
      if (e.target.closest('[data-cart-decrease]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-cart-decrease]');
        if (btn.closest('[data-cart-item-singleton]')) return;
        const key = btn.dataset.cartDecrease;
        const input = document.querySelector(`[data-cart-quantity="${key}"]`);
        if (input) {
          const newQty = Math.max(0, parseInt(input.value) - 1);
          await this.changeItemQuantity(key, newQty);
        }
      }
      
      if (e.target.closest('[data-cart-remove]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-cart-remove]');
        const key = btn.dataset.cartRemove;
        await this.changeItemQuantity(key, 0);
      }

      // Guarantee toggle
      if (e.target.closest('[data-guarantee-toggle]')) {
        e.preventDefault();
        const toggle = e.target.closest('[data-guarantee-toggle]');
        const block = toggle.closest('.guarantee-block');
        if (!block || block.classList.contains('guarantee-pending')) return;
        const variantId = block.getAttribute('data-guarantee-variant-id');
        const isActive = toggle.classList.contains('is-active');
        if (isActive) {
          await this.updateGuaranteeInCart(block, variantId, 0);
        } else {
          const qtyDisplay = block.querySelector('[data-guarantee-qty-display]');
          const qty = qtyDisplay ? parseInt(qtyDisplay.textContent, 10) || 1 : 1;
          await this.updateGuaranteeInCart(block, variantId, qty);
        }
      }

      if (e.target.closest('[data-guarantee-info-open]')) {
        e.preventDefault();
        if (window.YasminaGuaranteeModal && typeof window.YasminaGuaranteeModal.open === 'function') {
          window.YasminaGuaranteeModal.open();
        }
      }

      // Guarantee quantity increase
      if (e.target.closest('[data-guarantee-increase]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-guarantee-increase]');
        if (btn.classList.contains('is-disabled')) return;
        const block = btn.closest('.guarantee-block');
        if (!block || block.classList.contains('guarantee-pending')) return;
        const variantId = block.getAttribute('data-guarantee-variant-id');
        const maxQty = parseInt(block.getAttribute('data-guarantee-max-qty'), 10);
        const qtyDisplay = block.querySelector('[data-guarantee-qty-display]');
        const currentQty = parseInt(qtyDisplay.textContent, 10) || 1;
        const newQty = Math.min(currentQty + 1, maxQty);
        if (newQty !== currentQty) {
          await this.updateGuaranteeInCart(block, variantId, newQty);
        }
      }

      // Guarantee quantity decrease
      if (e.target.closest('[data-guarantee-decrease]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-guarantee-decrease]');
        if (btn.classList.contains('is-disabled')) return;
        const block = btn.closest('.guarantee-block');
        if (!block || block.classList.contains('guarantee-pending')) return;
        const variantId = block.getAttribute('data-guarantee-variant-id');
        const qtyDisplay = block.querySelector('[data-guarantee-qty-display]');
        const currentQty = parseInt(qtyDisplay.textContent, 10) || 1;
        const newQty = Math.max(1, currentQty - 1);
        if (newQty !== currentQty) {
          await this.updateGuaranteeInCart(block, variantId, newQty);
        }
      }
    });
    
    document.addEventListener('change', async (e) => {
      if (e.target.matches('[data-cart-quantity]')) {
        const input = e.target;
        if (input.closest('[data-cart-item-singleton]')) {
          input.value = 1;
          return;
        }
        const key = input.dataset.cartQuantity;
        const newQty = parseInt(input.value) || 0;
        await this.changeItemQuantity(key, newQty);
      }
    });

    if (!window.__cartHeaderBound) {
      window.__cartHeaderBound = true;
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('.header__cart, [data-cart-open]');
        if (!trigger) return;
        e.preventDefault();
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open(trigger);
        }
      });
    }
  }
  
  async updateGuaranteeInCart(block, variantId, quantity) {
    block.classList.add('guarantee-pending');
    const cart = window.__yasminaCartState;
    const items = (cart && Array.isArray(cart.items)) ? cart.items : [];

    // Find if this variant is already in cart
    const existingItem = items.find(item => String(item.variant_id) === String(variantId));

    try {
      let response;
      if (existingItem && quantity > 0) {
        // Update quantity
        const lineIndex = items.indexOf(existingItem) + 1;
        response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineIndex,
            quantity: quantity,
            sections: 'cart-drawer',
            sections_url: window.location.pathname
          })
        });
      } else if (existingItem && quantity === 0) {
        // Remove
        const lineIndex = items.indexOf(existingItem) + 1;
        response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineIndex,
            quantity: 0,
            sections: 'cart-drawer',
            sections_url: window.location.pathname
          })
        });
      } else if (!existingItem && quantity > 0) {
        // Add new
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: quantity }],
            sections: 'cart-drawer',
            sections_url: window.location.pathname
          })
        });
      } else {
        block.classList.remove('guarantee-pending');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to update guarantee');
      }

      const data = await response.json();
      // For /cart/add.js the response structure is different
      if (data.items && !data.item_count && data.items.length) {
        // This is an add response, need to fetch full cart
        const cartResp = await fetch('/cart.js');
        const fullCart = await cartResp.json();
        window.__yasminaCartState = fullCart;
        await this.updateCartUI(fullCart);
      } else {
        window.__yasminaCartState = data;
        if (data.sections && data.sections['cart-drawer']) {
          const html = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
          const targetElement = document.querySelector('cart-drawer');
          const sourceElement = html.querySelector('cart-drawer');
          if (targetElement && sourceElement) {
            targetElement.replaceChildren(...Array.from(sourceElement.childNodes).map(node => node.cloneNode(true)));
          }
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.updateCartCountDisplay(data.item_count);
            cartDrawer.updateCartTotalsDisplay(data.total_price);
            if (data.item_count === 0) cartDrawer.classList.add('is-empty');
            else cartDrawer.classList.remove('is-empty');
            cartDrawer.applySingletonCartLocks();
            cartDrawer.initGuaranteeBlocks();
            cartDrawer.syncFooterSpace();
          }
          if (window.cartPromocode) window.cartPromocode.loadSavedCode();
        } else {
          await this.updateCartUI(data);
        }
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: window.__yasminaCartState,
      });

    } catch (error) {
      console.error('Error updating guarantee:', error);
      if (window.NotificationManager) {
        window.NotificationManager.error(error.message || 'Failed to update guarantee');
      }
    } finally {
      block.classList.remove('guarantee-pending');
    }
  }

  async changeItemQuantity(key, quantity) {
    if (!key || this.pendingQuantityUpdates.has(key)) return;

    this.pendingQuantityUpdates.add(key);
    const line = parseInt(key, 10);
    const previousCartState = window.__yasminaCartState;
    const targetItemBeforeChange = this.getCartItemByKeyOrLine(previousCartState, key);

    try {
      let response;
      
      response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: Number.isFinite(line) ? line : key,
          quantity: quantity,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart update failed:', errorText);
        if (response.status === 429 || errorText.includes('Verifying your connection')) {
          throw new Error('Too many cart requests. Please wait a second and try again.');
        }
        if (errorText.includes('no valid id or line parameter')) {
          throw new Error('Cart line reference is invalid. Please refresh the page and try again.');
        }
        throw new Error('Failed to update cart');
      }

      const contentType = response.headers.get('content-type') || '';
      const isJsonLikeResponse =
        contentType.includes('application/json') ||
        contentType.includes('text/javascript') ||
        contentType.includes('application/javascript');
      if (!isJsonLikeResponse) {
        await response.text();
        throw new Error('Cart returned an unexpected response');
      }

      const responseText = await response.text();
      let cart;
      try {
        cart = JSON.parse(responseText);
      } catch (error) {
        throw new Error('Cart returned an unexpected response');
      }
      window.__yasminaCartState = cart;
      cart = await this.removeOrphanSubscriptionIfNeeded({
        cart,
        previousCart: previousCartState,
        removedItem: targetItemBeforeChange,
        requestedQuantity: quantity
      });
      window.__yasminaCartState = cart;
      cart = await this.removeOrphanGuaranteesIfNeeded(cart);
      window.__yasminaCartState = cart;

      if (cart.sections && cart.sections['cart-drawer']) {
        try {
          const html = new DOMParser().parseFromString(cart.sections['cart-drawer'], 'text/html');
          const targetElement = document.querySelector('cart-drawer');
          const sourceElement = html.querySelector('cart-drawer');
          if (targetElement && sourceElement) {
            targetElement.replaceChildren(...Array.from(sourceElement.childNodes).map(node => node.cloneNode(true)));
          }
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.updateCartCountDisplay === 'function') {
            cartDrawer.updateCartCountDisplay(cart.item_count);
            cartDrawer.updateCartTotalsDisplay(cart.total_price);
          }
          if (cartDrawer) {
            if (cart.item_count === 0) cartDrawer.classList.add('is-empty');
            else cartDrawer.classList.remove('is-empty');
          }
          const savedCode = window.SecurityUtils 
            ? window.SecurityUtils.getValidatedLocalStorage('checkout_discount_code')
            : localStorage.getItem('checkout_discount_code');
          const checkoutButtons = document.querySelectorAll('[data-checkout]');
          checkoutButtons.forEach(btn => {
            let href = '/checkout';
            if (savedCode) href = href + '?discount=' + encodeURIComponent(savedCode);
            btn.setAttribute('href', href);
          });
          if (window.cartPromocode) window.cartPromocode.loadSavedCode();
          if (cartDrawer && typeof cartDrawer.applySingletonCartLocks === 'function') {
            cartDrawer.applySingletonCartLocks();
          }
          if (cartDrawer && typeof cartDrawer.initGuaranteeBlocks === 'function') {
            cartDrawer.initGuaranteeBlocks();
          }
          if (cartDrawer && typeof cartDrawer.syncFooterSpace === 'function') {
            cartDrawer.syncFooterSpace();
          }
        } catch (e) {
          await this.updateCartUI(cart);
        }
      } else {
        await this.updateCartUI(cart);
      }
      
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: cart,
      });
      
    } catch (error) {
      console.error('Error updating cart:', error);
      if (window.NotificationManager) {
        window.NotificationManager.error(error.message || 'Failed to update cart');
      }

      if (
        error.message &&
        (
          error.message.includes('Too many cart requests') ||
          error.message.includes('Cart line reference is invalid')
        )
      ) {
        return;
      }

      try {
        const recoveryResponse = await fetch('/cart.js');
        const recoveryType = recoveryResponse.headers.get('content-type') || '';
        if (!recoveryResponse.ok || !recoveryType.includes('application/json')) {
          throw new Error('Cart recovery response was not JSON');
        }
        const cartData = await recoveryResponse.json();
        await this.updateCartUI(cartData);
      } catch (e) {
        console.error('Failed to recover cart state:', e);
      }
    } finally {
      this.pendingQuantityUpdates.delete(key);
    }
  }
  
  async updateCartUI(cart) {
    try {
      window.__yasminaCartState = cart;

      const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      
      const targetElement = document.querySelector('cart-drawer');
      const sourceElement = html.querySelector('cart-drawer');
      
      if (targetElement && sourceElement) {
        targetElement.replaceChildren(...Array.from(sourceElement.childNodes).map(node => node.cloneNode(true)));
      }
      
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.updateCartCountDisplay === 'function') {
        cartDrawer.updateCartCountDisplay(cart.item_count);
        cartDrawer.updateCartTotalsDisplay(cart.total_price);
      }

      if (cartDrawer) {
        if (cart.item_count === 0) {
          cartDrawer.classList.add('is-empty');
        } else {
          cartDrawer.classList.remove('is-empty');
        }
      }
      
      const savedCode = window.SecurityUtils 
        ? window.SecurityUtils.getValidatedLocalStorage('checkout_discount_code')
        : localStorage.getItem('checkout_discount_code');
      const checkoutButtons = document.querySelectorAll('[data-checkout]');
      checkoutButtons.forEach(btn => {
        let href = '/checkout';
        if (savedCode) {
          href = href + '?discount=' + encodeURIComponent(savedCode);
        }
        btn.setAttribute('href', href);
      });

      if (cart.item_count === 0 && savedCode) {
        localStorage.removeItem('checkout_discount_code');
        const discountList = document.querySelector('[data-applied-discounts]');
        if (discountList) {
          discountList.style.display = 'none';
          discountList.innerHTML = '';
        }
      }
      
      const cartUpdatedEvent = new CustomEvent('cart:updated', { detail: cart });
      document.dispatchEvent(cartUpdatedEvent);
      
      if (window.cartPromocode) {
        window.cartPromocode.loadSavedCode();
      }

      if (cartDrawer && typeof cartDrawer.applySingletonCartLocks === 'function') {
        cartDrawer.applySingletonCartLocks();
      }
      if (cartDrawer && typeof cartDrawer.initGuaranteeBlocks === 'function') {
        cartDrawer.initGuaranteeBlocks();
      }

    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  async onCartUpdate() {
    try {
      const cartData = await fetch('/cart.js').then(r => r.json());
      await this.updateCartUI(cartData);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  getSingletonVariantIds() {
    return Array.isArray(window.__yasminaSingletonVariantIds)
      ? window.__yasminaSingletonVariantIds.map((id) => String(id))
      : [];
  }

  getYangoBundleVariantIds() {
    const map = window.__yangoVariantMap;
    if (!map || typeof map !== 'object') return [];
    return Object.keys(map).map((variantId) => {
      const entry = map[variantId];
      return entry && entry.yangoId ? String(entry.yangoId) : null;
    }).filter(Boolean);
  }

  isWithYangoCartItem(item, yangoBundleIdsSet) {
    if (!item) return false;

    const properties = item.properties || {};
    const bundleMarker = String(properties._yp_bundle || properties.yp_bundle || '').toLowerCase();
    if (bundleMarker === 'with-yango') return true;

    const variantId = String(item.variant_id || '');
    if (yangoBundleIdsSet.has(variantId)) return true;

    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    const url = String(item.url || '').toLowerCase();
    const productTitle = String(item.product_title || item.title || '').toLowerCase();
    return (
      handle.includes('with-yango') ||
      url.includes('with-yango') ||
      productTitle.includes('with-yango') ||
      productTitle.includes('with yango')
    );
  }

  isYangoSubscriptionItem(item, singletonIdsSet) {
    if (!item) return false;

    const variantId = String(item.variant_id || '');
    if (singletonIdsSet && singletonIdsSet.size && singletonIdsSet.has(variantId)) return true;

    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    const url = String(item.url || '').toLowerCase();
    const productTitle = String(item.product_title || item.title || '').toLowerCase();
    return (
      handle === 'yango-plus' ||
      handle === 'yango-plus-1-month' ||
      url.includes('/products/yango-plus') ||
      url.includes('/products/yango-plus-1-month') ||
      productTitle.includes('yango plus')
    );
  }

  countWithYangoItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const yangoBundleIdsSet = new Set(this.getYangoBundleVariantIds());
    return cart.items.reduce((count, item) => {
      return count + (this.isWithYangoCartItem(item, yangoBundleIdsSet) ? 1 : 0);
    }, 0);
  }

  getCartItemByKeyOrLine(cart, key) {
    if (!cart || !Array.isArray(cart.items)) return null;

    const line = parseInt(key, 10);
    if (Number.isFinite(line) && line > 0 && line <= cart.items.length) {
      return cart.items[line - 1];
    }
    return cart.items.find((item) => String(item.key) === String(key)) || null;
  }

  findCartLineByPredicate(cart, predicate) {
    if (!cart || !Array.isArray(cart.items)) return null;
    for (let i = 0; i < cart.items.length; i += 1) {
      if (predicate(cart.items[i])) return i + 1;
    }
    return null;
  }

  async removeOrphanGuaranteesIfNeeded(cart) {
    if (!cart || !Array.isArray(cart.items)) return cart;

    const cartDrawer = document.querySelector('cart-drawer');
    const config = cartDrawer && typeof cartDrawer.getGuaranteeConfig === 'function'
      ? cartDrawer.getGuaranteeConfig()
      : null;
    if (!config) return cart;

    const allGuaranteeVariantIds = new Set(
      Object.values(config.variants).map(v => String(v.id))
    );

    // Count speaker quantities per type
    const speakerQtyByType = {};
    cart.items.forEach(item => {
      const handle = String(item.handle || item.product_handle || '').toLowerCase();
      const type = config.speakerType[handle];
      if (type) {
        speakerQtyByType[type] = (speakerQtyByType[type] || 0) + item.quantity;
      }
    });

    // Check each guarantee item — remove if no matching speakers or cap quantity
    let updatedCart = cart;
    for (let i = updatedCart.items.length - 1; i >= 0; i--) {
      const item = updatedCart.items[i];
      if (!allGuaranteeVariantIds.has(String(item.variant_id))) continue;

      // Determine which speaker type this guarantee is for
      let guaranteeType = null;
      for (const [handle, variantId] of Object.entries(config.speakerToGuarantee)) {
        if (String(variantId) === String(item.variant_id)) {
          guaranteeType = config.speakerType[handle];
          break;
        }
      }

      if (!guaranteeType) continue;

      const speakerQty = speakerQtyByType[guaranteeType] || 0;
      const neededQty = Math.min(item.quantity, speakerQty);

      if (neededQty === item.quantity) continue;

      // Need to update or remove this guarantee
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: i + 1,
          quantity: neededQty,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });

      if (response.ok) {
        try {
          updatedCart = await response.json();
          if (!updatedCart || !Array.isArray(updatedCart.items)) updatedCart = cart;
        } catch (e) {
          // ignore
        }
      }
    }

    return updatedCart;
  }

  async removeOrphanSubscriptionIfNeeded(params) {
    const cart = params && params.cart;
    const requestedQuantity = params && params.requestedQuantity;
    if (requestedQuantity !== 0) return cart;
    if (!cart || !Array.isArray(cart.items)) return cart;

    const singletonIdsSet = new Set(this.getSingletonVariantIds());
    const currentWithYangoCount = this.countWithYangoItems(cart);
    if (currentWithYangoCount > 0) return cart;

    const subscriptionLine = this.findCartLineByPredicate(
      cart,
      (item) => this.isYangoSubscriptionItem(item, singletonIdsSet)
    );
    if (!subscriptionLine) return cart;

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        line: subscriptionLine,
        quantity: 0,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) return cart;

    const contentType = response.headers.get('content-type') || '';
    const isJsonLikeResponse =
      contentType.includes('application/json') ||
      contentType.includes('text/javascript') ||
      contentType.includes('application/javascript');
    if (!isJsonLikeResponse) return cart;

    try {
      const updatedCart = await response.json();
      return updatedCart && Array.isArray(updatedCart.items) ? updatedCart : cart;
    } catch (error) {
      return cart;
    }
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

class CartPromocode {
  constructor() {
    this.form = document.getElementById('cart-discount-form');
    this.submitBtn = document.querySelector('.promocode__state-cart');
    this.promocodeInput = document.querySelector('.promocode__input');
    this.discountList = document.querySelector('[data-applied-discounts]');
    
    if (!this.form || !this.submitBtn || !this.promocodeInput) return;
    
    this.originalBtnText = this.submitBtn.textContent || 'Apply';
    this.savedCode = null;
    
    this.init();
  }
  
  init() {
    this.loadSavedCode();
    this.bindEvents();
    
    document.addEventListener('cart:updated', () => {
      this.updateCheckoutLinks();
    });
  }
  
  bindEvents() {
    this.submitBtn.addEventListener('click', () => this.applyPromocode());

    this.promocodeInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.applyPromocode();
      }
    });

    this.promocodeInput.addEventListener('input', () => {
      if (this.submitBtn.classList.contains('error')) {
        this.submitBtn.classList.remove('error');
        this.submitBtn.textContent = this.originalBtnText;
      }
    });

    if (!window.__promocodeRemoveHandlerBound) {
      window.__promocodeRemoveHandlerBound = true;
      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-remove-discount]');
        if (removeBtn && window.cartPromocode) {
          e.preventDefault();
          e.stopPropagation();
          window.cartPromocode.clearCode();
          window.cartPromocode.renderDiscountTag();
          window.cartPromocode.updateCheckoutLinks();

          if (window.cartPromocode.promocodeInput) {
            window.cartPromocode.promocodeInput.value = '';
          }

          if (window.cartPromocode.submitBtn) {
            window.cartPromocode.submitBtn.classList.remove('success', 'error');
            window.cartPromocode.submitBtn.textContent = window.cartPromocode.originalBtnText;
          }
        }
      });
    }
  }
  
  loadSavedCode() {
    this.savedCode = window.SecurityUtils 
      ? window.SecurityUtils.getValidatedLocalStorage('checkout_discount_code')
      : localStorage.getItem('checkout_discount_code');
    this.renderDiscountTag();
    this.updateCheckoutLinks();
  }
  
  saveCode(code) {
    if (window.SecurityUtils) {
      if (window.SecurityUtils.setValidatedLocalStorage('checkout_discount_code', code)) {
        this.savedCode = code;
      } else {
        console.warn('Invalid promocode format');
        return false;
      }
    } else {
      this.savedCode = code;
      localStorage.setItem('checkout_discount_code', code);
    }
    return true;
  }
  
  clearCode() {
    this.savedCode = null;
    localStorage.removeItem('checkout_discount_code');
    localStorage.removeItem('applied_discount_codes');
  }
  
  renderDiscountTag() {
    if (!this.discountList) return;

    if (!this.savedCode) {
      this.discountList.style.display = 'none';
      this.discountList.innerHTML = '';
      return;
    }

    const template = document.getElementById('discount-item-template');
    if (!template) return;

    this.discountList.style.display = 'flex';
    this.discountList.innerHTML = '';

    const clone = template.content.cloneNode(true);
    const item = clone.querySelector('.promocode__discount-item');
    const tag = clone.querySelector('.promocode__discount-tag');
    const removeBtn = clone.querySelector('.promocode__discount-remove');

    const sanitizedCode = window.SecurityUtils
      ? window.SecurityUtils.escapeHTML(this.savedCode)
      : this.savedCode;

    item.setAttribute('data-discount-code', sanitizedCode);
    tag.textContent = sanitizedCode;
    removeBtn.setAttribute('data-remove-discount', sanitizedCode);


    this.discountList.appendChild(clone);
  }
  
  
  updateCheckoutLinks() {
    const checkoutButtons = document.querySelectorAll('[href*="/checkout"], [data-checkout]');
    checkoutButtons.forEach(btn => {
      let href = btn.getAttribute('href') || '/checkout';
      href = href.split('?')[0];
      
      if (this.savedCode) {
        href = href + '?discount=' + encodeURIComponent(this.savedCode);
      }
      
      btn.setAttribute('href', href);
    });
  }

  applyPromocode() {
    this.submitBtn.classList.remove('success', 'error');
    const code = this.promocodeInput.value.trim();
    
    if (!code) {
      this.submitBtn.classList.add('error');
      this.submitBtn.textContent = 'Enter Code';
      setTimeout(() => {
        this.submitBtn.classList.remove('error');
        this.submitBtn.textContent = this.originalBtnText;
      }, 2000);
      return;
    }
    
    // Validate promocode format
    if (!/^[a-zA-Z0-9\-_]+$/.test(code)) {
      this.submitBtn.classList.add('error');
      this.submitBtn.textContent = 'Invalid Code';
      setTimeout(() => {
        this.submitBtn.classList.remove('error');
        this.submitBtn.textContent = this.originalBtnText;
      }, 2000);
      return;
    }
    
    if (this.savedCode && this.savedCode.toUpperCase() === code.toUpperCase()) {
      this.submitBtn.classList.add('error');
      this.submitBtn.textContent = 'Already Applied';
      setTimeout(() => {
        this.submitBtn.classList.remove('error');
        this.submitBtn.textContent = this.originalBtnText;
      }, 2000);
      return;
    }

    if (this.savedCode) {
      this.clearCode();
    }
    
    if (!this.saveCode(code)) {
      this.submitBtn.classList.add('error');
      this.submitBtn.textContent = 'Invalid Code';
      setTimeout(() => {
        this.submitBtn.classList.remove('error');
        this.submitBtn.textContent = this.originalBtnText;
      }, 2000);
      return;
    }
    
    this.renderDiscountTag();
    this.updateCheckoutLinks();
    
    this.submitBtn.classList.add('success');
    this.submitBtn.textContent = 'Applied';
    this.promocodeInput.value = '';
    
    setTimeout(() => {
      this.submitBtn.classList.remove('success');
      this.submitBtn.textContent = this.originalBtnText;
    }, 2000);
  }
}

function initCartRuntime() {
  const now = Date.now();
  if (window.__cartRuntimeRefreshAt && now - window.__cartRuntimeRefreshAt < 1500) {
    return;
  }
  window.__cartRuntimeRefreshAt = now;

  if (!window.cartPromocode) {
    window.cartPromocode = new CartPromocode();
  } else {
    window.cartPromocode.loadSavedCode();
  }
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  if (cartDrawerItems && typeof cartDrawerItems.updateCartUI === 'function') {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => cartDrawerItems.updateCartUI(cart))
      .catch(() => {});
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartRuntime);
} else {
  initCartRuntime();
}

window.addEventListener('pageshow', () => {
  initCartRuntime();
});

window.addEventListener('focus', () => {
  initCartRuntime();
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    initCartRuntime();
  }
});

window.addEventListener('popstate', () => {
  initCartRuntime();
});
